import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { AzureDataFactoryLinkedService, adfAuthTypes } from 'app/model/linkedservice/azure-data-factory.linkedservice';
import { Metamodel } from 'app/model/base/model';
import { ConnectionAuthKind, format } from 'shell-models-lib';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { compact } from 'lodash';

const adfSelectionSetting: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    label: stringResources.ADF_Workspace_SelectionMethod,
    description: stringResources.ADF_Workspace_SelectMethodDescription,
    options: azureResourceSelectMethodOptions,
    defaultValue: '{dataFactoryName != null ? "manual" : "azure"}',
    isLabelVertical: true,
    cases: [
        {
            caseValue: 'azure',
            children: [
                {
                    type: 'azure-data-factory-select',
                    bind: {
                        subscriptionId: 'subscriptionId',
                        resourceGroupName: 'resourceGroupName',
                        dataFactoryName: 'dataFactoryName'
                    },
                    validators: {
                        subscriptionId: [
                            { type: 'required', errorMessage: '' }
                        ],
                        resourceGroupName: [
                            { type: 'required', errorMessage: '' }
                        ],
                        dataFactoryName: [
                            { type: 'required', errorMessage: '' }
                        ]
                    }
                }
            ]
        },
        {
            caseValue: 'manual',
            children: [
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'subscriptionId',
                        label: stringResources.SubscriptionId,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'resourceGroupName',
                        label: stringResources.ModelName_ResourceGroupName,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'dataFactoryName',
                        label: stringResources.DataFactoryName,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        isLabelVertical: true
                    }
                }
            ]
        }
    ]
};

const tenantTextBoxSetting: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'tenant-textbox',
        bind: 'tenant',
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        label: stringResources.ModelName_Tenant,
        required: true
    }
};

const servicePrincipalIDSetting: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'servicePrincipalId',
        label: stringResources.ModelName_ServicePrincipalID,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    }
};

const servicePrincipalKeySetting: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    enableAKVParameterization: true,
    bind: 'servicePrincipalKey',
    label: stringResources.ModelName_ServicePrincipalKey,
    child: {
        type: 'parameterizable',
        attribute: 'servicePrincipalKey',
        child:
        {
            type: 'password',
            label: stringResources.ModelName_ServicePrincipalKey,
            required: true,
            isLabelVertical: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ]
        }
    },
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureDataFactory),
        stringResources.ModelName_ServicePrincipalKey)
};

export const azureDataFactoryMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: Metamodel.modelFor(AzureDataFactoryLinkedService).name,
        displayName: stringResources.AzureDataFactory,
        supportedInTrident: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'AzureDataFactory' as ResourceKind
        },
        supportOPDG: true,
        icon: {
            className: 'svg-i-20x20-adf'
        },
        category: ['Azure'],
        supportedAuthKindsInTrident: compact([
            ConnectionAuthKind.OAuth2,
            ConnectionAuthKind.ServicePrincipal,
            ConnectionAuthKind.WorkspaceIdentity
        ]),
        isCompute: true,
        isFirstClass: true,
        copySinkTypes: [],
        copySourceTypes: [],
        disableQueryAPI: true,
        supportFirstClassParameterExperience: true,
        controls: [
            adfSelectionSetting,
            {
                type: 'select-switch',
                bind: 'authenticationType',
                label: stringResources.Authentication_Method_Label,
                options: [
                    {
                        label: stringResources.AuthenticationType_ManagedServiceIdentity,
                        value: adfAuthTypes.MSIAuthentication
                    },
                    {
                        label: stringResources.AuthenticationType_ServicePrincipal,
                        value: adfAuthTypes.ServicePrincipalAuthentication
                    }
                ],
                isLabelVertical: true,
                defaultValue: adfAuthTypes.MSIAuthentication,
                cases: [
                    {
                        caseValue: adfAuthTypes.MSIAuthentication,
                        children: [
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.AzureDataFactory,
                                link: 'https://go.microsoft.com/fwlink/?linkid=2164350'
                            }
                        ]
                    },
                    {
                        caseValue: adfAuthTypes.ServicePrincipalAuthentication,
                        children: [
                            tenantTextBoxSetting,
                            servicePrincipalIDSetting,
                            servicePrincipalKeySetting
                        ]
                    }
                ]
            }
        ]
    };
    return metadata;
}
